import { REFERRALS_ENABLED, REFERRAL_NAV_ENABLED } from "@/lib/features";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, LayoutGrid, Heart, User, LogOut, Bug, Gift, Compass } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useDebugMode } from "@/hooks/useDebugMode";
import { useDebugPanel, toggleDebugPanel } from "@/hooks/useDebugPanel";
import { useGenerationQuota } from "@/hooks/useGenerationQuota";
import hueloLogo from "@/assets/huelo-logo.svg";

type NavItem = {
  label: string;
  to: string;
  tab?: string; // tab param when route is /dashboard
  icon: React.ComponentType<{ className?: string }>;
};

const NAV_ITEMS: NavItem[] = [
  { label: "Feed", to: "/feed", icon: Compass },
  { label: "My Designs", to: "/dashboard", tab: "designs", icon: LayoutGrid },
  { label: "Saved furniture", to: "/dashboard", tab: "saved", icon: Heart },
];



interface AppSidebarProps {
  onNavigate?: () => void;
}

const AppSidebar = ({ onNavigate }: AppSidebarProps) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const debugEnabled = useDebugMode();
  const debugOpen = useDebugPanel();

  const quota = useGenerationQuota();

  const currentTab = new URLSearchParams(search).get("tab") ?? "designs";

  const isActive = (item: NavItem) => {
    if (item.to === "/app") return pathname === "/app";
    if (item.to === "/dashboard") {
      const onDashboard = pathname === "/dashboard" || pathname.startsWith("/dashboard/design");
      return onDashboard && currentTab === item.tab;
    }
    return pathname === item.to;
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  const itemClass = (active: boolean) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-full text-xs font-semibold tracking-normal transition-colors ${
      active
        ? "bg-muted text-foreground"
        : "text-muted-foreground hover:text-foreground hover:bg-muted"
    }`;

  return (
    <div className="h-full w-full flex flex-col bg-background">
      <div className="px-5 pt-6 pb-8">
        <Link to="/dashboard" onClick={onNavigate} className="inline-block">
          <img src={hueloLogo} alt="Huelo" className="h-8" />
        </Link>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        <Link
          to="/app"
          onClick={onNavigate}
          className={`flex items-center gap-3 px-4 py-3 rounded-full text-xs font-semibold tracking-normal transition-all bg-primary-strong text-primary-strong-foreground shadow-elegant hover:opacity-90 ${
            pathname === "/app" ? "ring-2 ring-primary/30" : ""
          }`}
        >
          <Sparkles className="w-4 h-4 shrink-0" />
          <span>New Design</span>
        </Link>

        <div className="pt-3 mt-3 border-t border-border space-y-1">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item);
            const href = item.tab ? `${item.to}?tab=${item.tab}` : item.to;
            const Icon = item.icon;
            return (
              <Link
                key={`${item.to}-${item.tab ?? ""}`}
                to={href}
                onClick={onNavigate}
                className={itemClass(active)}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>



      <div className="p-3 border-t border-border space-y-1">
        {debugEnabled && (
          <button
            onClick={toggleDebugPanel}
            className={itemClass(debugOpen) + " w-full"}
          >
            <Bug className="w-4 h-4 shrink-0" />
            <span>{debugOpen ? "Hide debug" : "Show debug"}</span>
          </button>
        )}
        {REFERRALS_ENABLED && REFERRAL_NAV_ENABLED && (
        <Link
          to="/dashboard?tab=invite"
          onClick={onNavigate}
          className={itemClass(
            (pathname === "/dashboard" || pathname.startsWith("/dashboard/design")) &&
              currentTab === "invite",
          )}
        >
          <Gift className="w-4 h-4 shrink-0" />
          <span>Invite friends</span>
        </Link>
        )}
        <Link
          to="/dashboard?tab=account"
          onClick={onNavigate}
          className={itemClass(
            (pathname === "/dashboard" || pathname.startsWith("/dashboard/design")) &&
              currentTab === "account",
          )}
        >
          <User className="w-4 h-4 shrink-0" />
          <span>Account</span>
        </Link>
        <button
          onClick={() => {
            onNavigate?.();
            handleSignOut();
          }}
          className={itemClass(false) + " w-full"}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span>Sign out</span>
        </button>
      </div>

    </div>
  );
};

export default AppSidebar;
